/**
 * Lightweight event bus for cross-tab linking signals
 * (e.g. "scroll to meal plan X and highlight it").
 * Avoids prop-drilling through Index → MyKinTab → child components.
 */

type Listener<T> = (payload: T) => void;

class TinyEmitter<T> {
  private listeners = new Set<Listener<T>>();
  on(fn: Listener<T>) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  emit(payload: T) {
    this.listeners.forEach((fn) => fn(payload));
  }
}

export const myKinHighlight = new TinyEmitter<
  | { kind: "meal_plan"; id: string }
  | { kind: "recipe"; id: string }
  | { kind: "grocery_list"; id: string }
  | { kind: "meal_plan_renamed"; id: string; title: string }
  | { kind: "recipe_renamed"; id: string; title: string }
>();

/**
 * "Open My Kin to this section" intent — fired by the chat "Saved to My Kin"
 * confirmation toast's [View it] action so a just-saved item is one tap away.
 *
 * Two cases are covered:
 *  - My Kin NOT mounted (the common one: saving from the chat tab). We stash the
 *    intent in a latch, switch to the My Kin tab, and MyKinTab consumes the latch
 *    on mount.
 *  - My Kin already mounted. The live `myKinIntentBus` emit opens the section
 *    immediately.
 * Both reuse MyKinTab's existing sheet-open state + the `myKinHighlight` bus —
 * no new routing.
 */
export type MyKinIntent =
  | { section: "grocery"; highlight?: string }
  | { section: "recipes"; highlight?: { kind: "recipe" | "meal_plan"; id: string } }
  | { section: "todos" };

export const myKinIntentBus = new TinyEmitter<MyKinIntent>();

let pendingMyKinIntent: MyKinIntent | null = null;

export function openMyKinTo(intent: MyKinIntent) {
  pendingMyKinIntent = intent;
  // Switch to the My Kin tab (reuses Index's existing kin:navigate-tab listener).
  window.dispatchEvent(new CustomEvent("kin:navigate-tab", { detail: { tab: "mykin" } }));
  // If MyKinTab is already mounted, open the section right away.
  myKinIntentBus.emit(intent);
}

/** Read-and-clear the pending intent (MyKinTab calls this on mount). */
export function consumePendingMyKinIntent(): MyKinIntent | null {
  const intent = pendingMyKinIntent;
  pendingMyKinIntent = null;
  return intent;
}

